// Search-state for the orders index route. The route in AppRouter.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so consumers
// don't touch the router primitives directly. Status / salesperson / search
// / sort changes reset page so previous-state pagination never bleeds into
// the new view.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

// Internal
import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import { ORDER_STATUS_FILTER_VALUES } from '../../components/Sales/OrderManagement/types';

export interface OrderSearchParams {
  orderStatuses: string[];
  salespersonIds: string[];
  q: string;
  page: number;
  perPage: number;
  sort: string;
  direction: 'asc' | 'desc';
}

export const ORDERS_SEARCH_DEFAULTS: OrderSearchParams = {
  orderStatuses: [],
  salespersonIds: [],
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: 'created_at',
  direction: 'desc',
};

const ORDERS_ROUTE = '/orders' as const;

const VALID_STATUSES: ReadonlySet<string> = new Set(ORDER_STATUS_FILTER_VALUES);
const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);

function toStringArray(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string' && raw.length > 0) return raw.split(',').filter(Boolean);
  return [];
}

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseOrdersSearch(raw: Record<string, unknown>): OrderSearchParams {
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  const direction = VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
    ? (raw.direction as 'asc' | 'desc')
    : ORDERS_SEARCH_DEFAULTS.direction;
  return {
    orderStatuses: toStringArray(raw.orderStatuses).filter((s) => VALID_STATUSES.has(s)),
    salespersonIds: toStringArray(raw.salespersonIds),
    q: typeof raw.q === 'string' ? raw.q : ORDERS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : ORDERS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : ORDERS_SEARCH_DEFAULTS.perPage,
    sort: typeof raw.sort === 'string' && raw.sort.length > 0 ? raw.sort : ORDERS_SEARCH_DEFAULTS.sort,
    direction,
  };
}

export function useOrdersRouterSearch() {
  const search = useSearch({ from: ORDERS_ROUTE });
  const tanNavigate = useNavigate({ from: ORDERS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<OrderSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setOrderStatuses: (orderStatuses: string[]) => navigate({ orderStatuses, page: 1 }),
      setSalespersons: (salespersonIds: string[]) => navigate({ salespersonIds, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: string, direction: 'asc' | 'desc') => navigate({ sort, direction, page: 1 }),
    }),
    [search, navigate],
  );
}
